import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { CheckCircle, Calendar, Dog, ArrowRight, Home } from "lucide-react";

const ThankYouPage = () => {
  const location = useLocation();
  const [enrollmentData, setEnrollmentData] = useState(null);

  useEffect(() => {
    // Get enrollment data from navigation state
    if (location.state?.enrollment) {
      setEnrollmentData(location.state.enrollment);
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-off-white" data-testid="thank-you-page">
      {/* Header */}
      <header className="bg-white border-b border-olive/10">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <Link to="/" className="font-heading text-xl text-charcoal">
            The Pack Standard
            <span className="block text-sm text-olive">Puppy Academy</span>
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          {/* Success Icon */}
          <div className="w-24 h-24 bg-olive/10 rounded-full flex items-center justify-center mx-auto mb-8">
            <CheckCircle className="w-12 h-12 text-olive" strokeWidth={1.5} />
          </div>

          {/* Main Message */}
          <h1 className="font-heading text-4xl md:text-5xl text-charcoal mb-4">
            Thank You!
          </h1>
          <p className="text-xl text-olive font-medium mb-6">
            Your booking has been confirmed
          </p>

          {/* Welcome Message */}
          <div className="bg-white rounded-2xl p-8 mb-8 text-left border border-olive/10">
            <p className="text-lg text-charcoal leading-relaxed mb-4">
              <strong className="font-heading">The Pack Standard - Puppy Academy</strong> looks forward to meeting and helping your puppy on their training journey.
            </p>
            <p className="text-stone leading-relaxed mb-4">
              We're excited to welcome {enrollmentData?.puppy_name || "your puppy"} to our program. Together, we'll work on building confidence, good manners, and a strong foundation for a happy, well-balanced life.
            </p>
            <p className="text-stone leading-relaxed">
              A confirmation email has been sent to your registered email address with all the details of your booking.
            </p>
          </div>

          {/* What's Next */}
          <div className="bg-charcoal text-white rounded-2xl p-8 mb-8 text-left">
            <h2 className="font-heading text-2xl mb-6">What happens next?</h2>
            <ul className="space-y-4">
              <li className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-olive/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-olive font-medium">1</span>
                </div>
                <div>
                  <p className="font-medium">Confirmation Email</p>
                  <p className="text-white/70 text-sm">Check your inbox for booking details and program information.</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-olive/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-olive font-medium">2</span>
                </div>
                <div>
                  <p className="font-medium">Pre-Program Information</p>
                  <p className="text-white/70 text-sm">We'll be in touch before your start date with everything you need to know.</p>
                </div>
              </li>
              <li className="flex items-start gap-4">
                <div className="w-8 h-8 rounded-full bg-olive/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-olive font-medium">3</span>
                </div>
                <div>
                  <p className="font-medium">First Day</p>
                  <p className="text-white/70 text-sm">We'll collect your puppy from home and begin their training adventure!</p>
                </div>
              </li>
            </ul>
          </div>

          {/* Booking Summary */}
          {enrollmentData && (
            <div className="bg-sand rounded-2xl p-6 mb-8">
              <h3 className="font-heading text-lg text-charcoal mb-4">Booking Summary</h3>
              <div className="grid md:grid-cols-2 gap-4 text-left">
                <div className="flex items-center gap-3">
                  <Dog className="w-5 h-5 text-olive" />
                  <div>
                    <p className="text-sm text-stone">Puppy</p>
                    <p className="text-charcoal font-medium">{enrollmentData.puppy_name}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Calendar className="w-5 h-5 text-olive" />
                  <div>
                    <p className="text-sm text-stone">Program</p>
                    <p className="text-charcoal font-medium">{enrollmentData.program_name}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/dashboard">
              <Button className="btn-primary text-lg">
                View Your Dashboard
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/">
              <Button className="btn-outline text-lg">
                <Home className="mr-2 w-5 h-5" />
                Back to Home
              </Button>
            </Link>
          </div>
        </motion.div>
      </main>
    </div>
  );
};

export default ThankYouPage;
